/**
 * Utility functions for appointments
 * 
 * This module provides utility functions for working with appointments,
 * including date and time formatting.
 */

/**
 * Format a timestamp to a time string (HH:MM AM/PM)
 * @param {Object} timestamp - Firestore timestamp object or serialized timestamp
 * @returns {string} Formatted time string
 */
export const formatTime = (timestamp) => {
  if (!timestamp) {
    console.error('Missing timestamp');
    return 'Invalid time';
  }
  
  try {
    let date;
    
    // Handle different timestamp formats
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      // Firebase Timestamp object with toDate method
      date = timestamp.toDate();
    } else if (timestamp.seconds !== undefined && timestamp.nanoseconds !== undefined) {
      // Plain object with seconds and nanoseconds (serialized Firestore timestamp)
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      // JavaScript Date object
      date = timestamp;
    } else {
      console.error('Invalid timestamp format:', timestamp);
      return 'Invalid time';
    }
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * Format a date string (YYYY-MM-DD) to a more readable format (DD/MM/YYYY)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    console.error('Invalid date string:', dateString);
    return 'Invalid date';
  }
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return 'Invalid date';
    }
    
    return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format a date string (YYYY-MM-DD) to a full date format (e.g., Monday, 1 January 2023)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatDateFull = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    console.error('Invalid date string:', dateString);
    return 'Invalid date';
  }
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return 'Invalid date';
    }
    
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is in the past
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if the date is in the past, false otherwise
 */
export const isDateInPast = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return false;
    }
    
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return date < today;
  } catch (error) {
    console.error('Error checking if date is in past:', error);
    return false;
  }
};

/**
 * Get a date string for a date that is a specified number of days from today
 * @param {number} days - Number of days from today (positive for future, negative for past)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getDateStringFromToday = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Calculate the duration between two timestamps in minutes
 * @param {Object} startTime - Firestore timestamp object or serialized timestamp for the start time
 * @param {Object} endTime - Firestore timestamp object or serialized timestamp for the end time
 * @returns {number} Duration in minutes
 */
export const calculateDurationInMinutes = (startTime, endTime) => {
  if (!startTime || !endTime) {
    console.error('Missing timestamps:', startTime, endTime);
    return 0;
  }
  
  try {
    let startDate, endDate;
    
    // Handle different timestamp formats for startTime
    if (startTime.toDate && typeof startTime.toDate === 'function') {
      // Firebase Timestamp object with toDate method
      startDate = startTime.toDate();
    } else if (startTime.seconds !== undefined && startTime.nanoseconds !== undefined) {
      // Plain object with seconds and nanoseconds (serialized Firestore timestamp)
      startDate = new Date(startTime.seconds * 1000);
    } else if (startTime instanceof Date) {
      // JavaScript Date object
      startDate = startTime;
    } else {
      console.error('Invalid startTime format:', startTime);
      return 0;
    }
    
    // Handle different timestamp formats for endTime
    if (endTime.toDate && typeof endTime.toDate === 'function') {
      // Firebase Timestamp object with toDate method
      endDate = endTime.toDate();
    } else if (endTime.seconds !== undefined && endTime.nanoseconds !== undefined) {
      // Plain object with seconds and nanoseconds (serialized Firestore timestamp)
      endDate = new Date(endTime.seconds * 1000);
    } else if (endTime instanceof Date) {
      // JavaScript Date object
      endDate = endTime;
    } else {
      console.error('Invalid endTime format:', endTime);
      return 0;
    }
    
    const durationMs = endDate - startDate;
    const durationMinutes = Math.round(durationMs / (1000 * 60));
    
    return durationMinutes;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 0;
  }
};
